import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketInputSchema, serviceInputSchema, type LocaleView, type MarketInput, type ServiceInput } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextAreaField, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, get, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { CheckField, errText, issueMap, serverErrors, type Errors } from './common.tsx';

type Market = MarketInput;
type Service = ServiceInput;

// ---------- Markets ----------
export function MarketsTab() {
  const { can } = useAuth();
  const manage = can('settings.manage');
  const [editing, setEditing] = useState<{ market: Market | null } | null>(null);
  const q = useQuery<Market[], ApiClientError>({ queryKey: ['site-markets'], queryFn: () => get('/admin/site/markets') });
  return (
    <section className="panel" aria-labelledby="mk-h">
      <div className="panel__head">
        <div><h2 id="mk-h">Markets</h2><p className="ag-muted">Where the agency sells: countries, default language, currency and timezone.</p></div>
        {manage ? <Button variant="primary" icon="plus" onClick={() => setEditing({ market: null })}>Add market</Button> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={160} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="No markets yet" icon="globe" action={manage ? <Button variant="primary" icon="plus" onClick={() => setEditing({ market: null })}>Add market</Button> : undefined}>Add the first market to say where you sell.</EmptyState>
      ) : (
        <div className="ag-table-wrap" {...scrollRegion('Markets')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Markets</caption>
            <thead><tr><th scope="col">Market</th><th scope="col">Countries</th><th scope="col">Language</th><th scope="col">Currency</th><th scope="col">Timezone</th><th scope="col">Status</th>{manage ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((m) => (
                <tr key={m.code}>
                  <td><span className="ag-cell-2"><span>{m.name}</span><span className="ag-mono">{m.code}</span></span></td>
                  <td className="ag-mono">{m.countries.join(', ')}</td>
                  <td className="ag-mono">{m.defaultLocale}</td>
                  <td>{m.currency}</td>
                  <td>{m.timezone}</td>
                  <td><span className="seo-badges"><Badge tone={m.enabled ? 'success' : 'neutral'}>{m.enabled ? 'Enabled' : 'Disabled'}</Badge>{m.advertisingEnabled ? <Badge tone="info">Advertising</Badge> : null}</span></td>
                  {manage ? <td style={{ textAlign: 'right' }}><Button size="sm" onClick={() => setEditing({ market: m })} aria-label={`Edit ${m.name}`}>Edit</Button></td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted">You can view markets but not change them.</p> : null}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.market ? `Edit ${editing.market.name}` : 'Add market'} footer={null}>
        {editing ? <MarketForm key={editing.market?.code ?? 'new'} market={editing.market} onClose={() => setEditing(null)} /> : null}
      </Drawer>
    </section>
  );
}

function MarketForm({ market, onClose }: { market: Market | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const locales = useQuery<LocaleView[], ApiClientError>({ queryKey: ['locale-options', 'settings'], queryFn: () => get('/admin/site/locale-options') });
  const [v, setV] = useState({ code: market?.code ?? '', name: market?.name ?? '', countries: market?.countries.join(', ') ?? '', defaultLocale: market?.defaultLocale ?? '', currency: market?.currency ?? '', timezone: market?.timezone ?? '', enabled: market?.enabled ?? false, advertisingEnabled: market?.advertisingEnabled ?? false });
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: MarketInput) => put<Market>('/admin/site/markets', body),
    onSuccess: async (r) => { await qc.invalidateQueries({ queryKey: ['site-markets'] }); await qc.invalidateQueries({ queryKey: ['markets'] }); toast({ tone: 'success', message: `${r.name} saved.` }); onClose(); },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });
  const f = (k: 'code' | 'name' | 'timezone') => ({ value: v[k], error: errors[k], onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }) });
  const countriesError = errors.countries ?? Object.entries(errors).find(([k]) => k.startsWith('countries.'))?.[1];
  function submit() {
    setError(null);
    const p = marketInputSchema.safeParse({ ...v, code: v.code.trim().toUpperCase(), currency: v.currency.trim().toUpperCase(), countries: v.countries.split(/[\s,]+/).filter(Boolean).map((c) => c.toUpperCase()) });
    if (!p.success) { setErrors(issueMap(p.error.issues)); return; }
    setErrors({});
    save.mutate(p.data);
  }
  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title="Could not save the market">{error}</Notice> : null}
      <TextField data-autofocus label="Code" required readOnly={!!market} hint={market ? 'The code cannot be changed.' : 'A short uppercase code like DE or UK'} {...f('code')} />
      <TextField label="Name" required {...f('name')} />
      <TextField label="Countries" required value={v.countries} error={countriesError} hint="Two-letter country codes separated by commas, for example DE, AT, CH" onChange={(e) => setV({ ...v, countries: e.target.value })} />
      {locales.isError ? <Notice tone="error" title="Could not load languages">{errText(locales.error)}</Notice> : null}
      <SelectField label="Default language" required value={v.defaultLocale} error={errors.defaultLocale} disabled={locales.isLoading} onChange={(e) => setV({ ...v, defaultLocale: e.target.value })}>
        <option value="">Choose a language</option>
        {(locales.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code}){l.enabled ? '' : ' - disabled'}</option>)}
        {v.defaultLocale && !(locales.data ?? []).some((l) => l.code === v.defaultLocale) ? <option value={v.defaultLocale}>{v.defaultLocale}</option> : null}
      </SelectField>
      <TextField label="Currency" required value={v.currency} error={errors.currency} hint="Three capital letters, for example EUR" onChange={(e) => setV({ ...v, currency: e.target.value })} />
      <TextField label="Timezone" required hint="For example Europe/Berlin. Used for this market’s reporting." {...f('timezone')} />
      <CheckField label="Enabled" checked={v.enabled} onChange={(enabled) => setV({ ...v, enabled })} />
      <CheckField label="Advertising enabled" checked={v.advertisingEnabled} onChange={(advertisingEnabled) => setV({ ...v, advertisingEnabled })} hint="Marks the market as available for paid campaigns. It does not publish anything." />
      <div className="actions__row">
        <Button type="submit" variant="primary" loading={save.isPending}>{market ? 'Save changes' : 'Add market'}</Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}

// ---------- Services ----------
export function ServicesTab() {
  const { can } = useAuth();
  const manage = can('settings.manage');
  const [editing, setEditing] = useState<{ service: Service | null } | null>(null);
  const q = useQuery<Service[], ApiClientError>({ queryKey: ['site-services'], queryFn: () => get('/admin/site/services') });
  return (
    <section className="panel" aria-labelledby="svc-h">
      <div className="panel__head">
        <div><h2 id="svc-h">Services</h2><p className="ag-muted">What the agency sells. Landing pages can be attached to a service.</p></div>
        {manage ? <Button variant="primary" icon="plus" onClick={() => setEditing({ service: null })}>Add service</Button> : null}
      </div>
      {q.isLoading ? <Skeleton width="100%" height={160} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="No services yet" icon="layout" action={manage ? <Button variant="primary" icon="plus" onClick={() => setEditing({ service: null })}>Add service</Button> : undefined}>Add the first service so landing pages can be attached to it.</EmptyState>
      ) : (
        <div className="ag-table-wrap" {...scrollRegion('Services')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Services</caption>
            <thead><tr><th scope="col">Service</th><th scope="col">Description</th><th scope="col">Status</th>{manage ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((s) => (
                <tr key={s.key}>
                  <td><span className="ag-cell-2"><span>{s.name}</span><span className="ag-mono">{s.key}</span></span></td>
                  <td style={{ maxWidth: 420 }}>{s.description}</td>
                  <td><Badge tone={s.enabled ? 'success' : 'neutral'}>{s.enabled ? 'Enabled' : 'Disabled'}</Badge></td>
                  {manage ? <td style={{ textAlign: 'right' }}><Button size="sm" onClick={() => setEditing({ service: s })} aria-label={`Edit ${s.name}`}>Edit</Button></td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted">You can view services but not change them.</p> : null}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.service ? `Edit ${editing.service.name}` : 'Add service'} footer={null}>
        {editing ? <ServiceForm key={editing.service?.key ?? 'new'} service={editing.service} onClose={() => setEditing(null)} /> : null}
      </Drawer>
    </section>
  );
}

function ServiceForm({ service, onClose }: { service: Service | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({ key: service?.key ?? '', name: service?.name ?? '', description: service?.description ?? '', enabled: service?.enabled ?? true });
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: ServiceInput) => put<Service>('/admin/site/services', body),
    onSuccess: async (r) => { await qc.invalidateQueries({ queryKey: ['site-services'] }); await qc.invalidateQueries({ queryKey: ['services'] }); toast({ tone: 'success', message: `${r.name} saved.` }); onClose(); },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });
  function submit() {
    setError(null);
    const p = serviceInputSchema.safeParse(v);
    if (!p.success) { setErrors(issueMap(p.error.issues)); return; }
    setErrors({});
    save.mutate(p.data);
  }
  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title="Could not save the service">{error}</Notice> : null}
      <TextField data-autofocus label="Key" required readOnly={!!service} value={v.key} error={errors.key} hint={service ? 'The key cannot be changed.' : 'Lowercase letters, digits and hyphens, starting with a letter, for example web-development'} onChange={(e) => setV({ ...v, key: e.target.value })} />
      <TextField label="Name" required value={v.name} error={errors.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
      <TextAreaField label="Description" required rows={3} value={v.description} error={errors.description} hint={`${v.description.length}/280`} onChange={(e) => setV({ ...v, description: e.target.value })} />
      <CheckField label="Enabled" checked={v.enabled} onChange={(enabled) => setV({ ...v, enabled })} />
      <div className="actions__row">
        <Button type="submit" variant="primary" loading={save.isPending}>{service ? 'Save changes' : 'Add service'}</Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
